import axios from 'axios';

import { LogService } from './log-service';

import type { AxiosResponse } from 'axios';

const SETTINGS_BASE_URL = '/loss-prevention';
const FRAUD_BASE_URL = '/fraud';

// Default values for loss prevention settings
const DEFAULT_HIGH_VALUE_THRESHOLD_USD = 500;
const DEFAULT_MAX_PREVIOUS_ORDERS = 3;
const DEFAULT_VELOCITY_MAX_ORDERS = 3;
const DEFAULT_VELOCITY_TIME_WINDOW_HOURS = 24;
const DEFAULT_UNUSUAL_ORDER_TIME_START_HOUR = 3;
const DEFAULT_UNUSUAL_ORDER_TIME_END_HOUR = 5;
const DEFAULT_FUZZY_FLAG_THRESHOLD = 85;
const DEFAULT_FUZZY_BLOCK_THRESHOLD = 95;

type HighValueThresholdSettings = {
  enabled: boolean;
  orderValueThresholdUsd: number;
  maxPreviousOrders: number;
};

type VelocityAlertsSettings = {
  enabled: boolean;
  maxOrdersPerAddress: number;
  timeWindowHours: number;
};

type AddressBlacklistSettings = {
  enabled: boolean;
  enableFuzzyMatching: boolean;
  fuzzyFlagThreshold: number;
  fuzzyBlockThreshold: number;
};

type UnusualOrderTimeSettings = {
  enabled: boolean;
  startHour: number;
  endHour: number;
};

type KnownAddressSettings = {
  enabled: boolean;
};

type RushShippingSettings = {
  enabled: boolean;
};

type InternationalAddressMismatchSettings = {
  enabled: boolean;
};

export type LossPreventionSettings = {
  isEnabled: boolean;
  highValueThreshold: HighValueThresholdSettings;
  velocityAlerts: VelocityAlertsSettings;
  addressBlacklist: AddressBlacklistSettings;
  unusualOrderTime: UnusualOrderTimeSettings;
  knownAddress: KnownAddressSettings;
  rushShipping: RushShippingSettings;
  internationalAddressMismatch: InternationalAddressMismatchSettings;
  _backendMetadata?: {
    sendEmailOnHold?: boolean;
    lossPreventionSettingsID?: number;
    lastModifiedDateUtc?: string;
  };
};

export type BackendLossPreventionSettings = {
  lossPreventionSettingsID?: number;
  isEnabled: boolean;
  blacklistDetectionEnabled: boolean;
  blacklistFuzzyMatchEnabled: boolean;
  blacklistFuzzyMinThreshold: number;
  blacklistFuzzyBlockThreshold: number;
  highValueNewCustomerEnabled: boolean;
  addressMismatchEnabled: boolean;
  velocityAlertEnabled: boolean;
  unusualOrderTimeEnabled: boolean;
  rushShippingFirstOrderEnabled: boolean;
  sendEmailOnHold: boolean;
  highValueThreshold: number;
  newCustomerMaxOrders: number;
  velocityMaxOrders: number;
  velocityTimeWindowHours: number;
  unusualOrderTimeStartHour: number;
  unusualOrderTimeEndHour: number;
  knownAddressTypeEnabled: boolean;
  lastModifiedDateUtc?: string;
};

type BlacklistAddress = {
  blacklistedAddressID?: number;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  reason?: string;
  referenceOrderId?: number;
  createdUtc?: string;
  modifiedUtc?: string;
  modifiedByUserID?: string;
};

type RemoveFraudTagResult = {
  success: boolean;
  orderReleased: boolean;
  newStatus: string;
  message: string;
};

/**
 * Loss Prevention Settings Service
 * Manages fraud detection rules and blacklist settings
 */
export const LossPreventionService = {
  /**
   * Get loss prevention settings for the current seller
   * @returns {Promise<LossPreventionSettings>} Settings object
   */
  getSettings: async (): Promise<LossPreventionSettings> => {
    try {
      const response: AxiosResponse<BackendLossPreventionSettings> = await axios.get(`${SETTINGS_BASE_URL}/settings`);
      const backendData = response?.data;

      // Transform backend flat structure to frontend nested structure
      return {
        isEnabled: backendData?.isEnabled ?? false,
        highValueThreshold: {
          enabled: backendData?.highValueNewCustomerEnabled ?? false,
          orderValueThresholdUsd: backendData?.highValueThreshold ?? DEFAULT_HIGH_VALUE_THRESHOLD_USD,
          maxPreviousOrders: backendData?.newCustomerMaxOrders ?? DEFAULT_MAX_PREVIOUS_ORDERS,
        },
        velocityAlerts: {
          enabled: backendData?.velocityAlertEnabled ?? false,
          maxOrdersPerAddress: backendData?.velocityMaxOrders ?? DEFAULT_VELOCITY_MAX_ORDERS,
          timeWindowHours: backendData?.velocityTimeWindowHours ?? DEFAULT_VELOCITY_TIME_WINDOW_HOURS,
        },
        addressBlacklist: {
          enabled: backendData?.blacklistDetectionEnabled ?? false,
          enableFuzzyMatching: backendData?.blacklistFuzzyMatchEnabled ?? false,
          fuzzyFlagThreshold: backendData?.blacklistFuzzyMinThreshold ?? DEFAULT_FUZZY_FLAG_THRESHOLD,
          fuzzyBlockThreshold: backendData?.blacklistFuzzyBlockThreshold ?? DEFAULT_FUZZY_BLOCK_THRESHOLD,
        },
        unusualOrderTime: {
          enabled: backendData?.unusualOrderTimeEnabled ?? false,
          startHour: backendData?.unusualOrderTimeStartHour ?? DEFAULT_UNUSUAL_ORDER_TIME_START_HOUR,
          endHour: backendData?.unusualOrderTimeEndHour ?? DEFAULT_UNUSUAL_ORDER_TIME_END_HOUR,
        },
        knownAddress: {
          // Backend uses "knownAddressTypeEnabled" instead of "knownAddressEnabled"
          enabled: backendData?.knownAddressTypeEnabled ?? false,
        },
        rushShipping: {
          enabled: backendData?.rushShippingFirstOrderEnabled ?? false,
        },
        internationalAddressMismatch: {
          enabled: backendData?.addressMismatchEnabled ?? false,
        },
        // Preserve backend-only fields for round-trip
        _backendMetadata: {
          sendEmailOnHold: backendData?.sendEmailOnHold ?? true,
          lossPreventionSettingsID: backendData?.lossPreventionSettingsID,
          lastModifiedDateUtc: backendData?.lastModifiedDateUtc,
        },
      };
    } catch (error) {
      LogService.logError(new Error('Error fetching loss prevention settings', { cause: error }));
      throw error;
    }
  },

  /**
   * Update loss prevention settings
   * @param {LossPreventionSettings} settings - Settings to update
   * @returns {Promise<BackendLossPreventionSettings>} Updated settings response
   */
  updateSettings: async (settings: LossPreventionSettings): Promise<BackendLossPreventionSettings> => {
    // Transform frontend nested structure to backend flat structure
    const backendPayload: BackendLossPreventionSettings = {
      isEnabled: settings.isEnabled ?? false,
      blacklistDetectionEnabled: settings.addressBlacklist?.enabled ?? false,
      blacklistFuzzyMatchEnabled: settings.addressBlacklist?.enableFuzzyMatching ?? false,
      blacklistFuzzyMinThreshold: settings.addressBlacklist?.fuzzyFlagThreshold ?? DEFAULT_FUZZY_FLAG_THRESHOLD,
      blacklistFuzzyBlockThreshold: settings.addressBlacklist?.fuzzyBlockThreshold ?? DEFAULT_FUZZY_BLOCK_THRESHOLD,
      highValueNewCustomerEnabled: settings.highValueThreshold?.enabled ?? false,
      addressMismatchEnabled: settings.internationalAddressMismatch?.enabled ?? false,
      velocityAlertEnabled: settings.velocityAlerts?.enabled ?? false,
      unusualOrderTimeEnabled: settings.unusualOrderTime?.enabled ?? false,
      rushShippingFirstOrderEnabled: settings.rushShipping?.enabled ?? false,
      highValueThreshold: settings.highValueThreshold?.orderValueThresholdUsd ?? DEFAULT_HIGH_VALUE_THRESHOLD_USD,
      newCustomerMaxOrders: settings.highValueThreshold?.maxPreviousOrders ?? DEFAULT_MAX_PREVIOUS_ORDERS,
      velocityMaxOrders: settings.velocityAlerts?.maxOrdersPerAddress ?? DEFAULT_VELOCITY_MAX_ORDERS,
      velocityTimeWindowHours: settings.velocityAlerts?.timeWindowHours ?? DEFAULT_VELOCITY_TIME_WINDOW_HOURS,
      unusualOrderTimeStartHour: settings.unusualOrderTime?.startHour ?? DEFAULT_UNUSUAL_ORDER_TIME_START_HOUR,
      unusualOrderTimeEndHour: settings.unusualOrderTime?.endHour ?? DEFAULT_UNUSUAL_ORDER_TIME_END_HOUR,
      // Backend uses "knownAddressTypeEnabled" instead of "knownAddressEnabled"
      knownAddressTypeEnabled: settings.knownAddress?.enabled ?? false,
      // Include backend-only fields from preserved metadata
      sendEmailOnHold: settings._backendMetadata?.sendEmailOnHold ?? true,
      lossPreventionSettingsID: settings._backendMetadata?.lossPreventionSettingsID,
      lastModifiedDateUtc: settings._backendMetadata?.lastModifiedDateUtc,
    };

    try {
      const response: AxiosResponse<BackendLossPreventionSettings> = await axios.post(`${SETTINGS_BASE_URL}/settings`, backendPayload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response?.data;
    } catch (error) {
      LogService.logError(new Error('Error updating loss prevention settings', { cause: error }));
      throw error;
    }
  },

  /**
   * Get blacklist entries for the current seller
   * @returns {Promise<BlacklistAddress[]>} Array of blacklisted addresses
   */
  getBlacklist: async (): Promise<BlacklistAddress[]> => {
    try {
      const response: AxiosResponse<BlacklistAddress[]> = await axios.get(`${FRAUD_BASE_URL}/blacklist`);
      return response?.data ?? [];
    } catch (error) {
      LogService.logError(new Error('Error fetching blacklist', { cause: error }));
      throw error;
    }
  },

  /**
   * Add address to blacklist
   * @param {BlacklistAddress} address - Address to blacklist
   * @returns {Promise<{blacklistedAddressID: number}>} Created blacklist entry
   */
  addToBlacklist: async (address: BlacklistAddress): Promise<{ blacklistedAddressID: number }> => {
    try {
      const response: AxiosResponse<{ blacklistedAddressID: number }> = await axios.post(`${FRAUD_BASE_URL}/blacklist`, address, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data ?? { blacklistedAddressID: 0 };
    } catch (error) {
      LogService.logError(new Error('Error adding address to blacklist', { cause: error }));
      throw error;
    }
  },

  /**
   * Update blacklist entry
   * @param {number} blacklistId - ID of blacklist entry to update
   * @param {Partial<BlacklistAddress>} updates - Fields to update
   * @returns {Promise<{success: boolean}>} Updated blacklist entry response
   */
  updateBlacklistEntry: async (blacklistId: number, updates: Partial<BlacklistAddress>): Promise<{ success: boolean }> => {
    try {
      const response: AxiosResponse<{ success: boolean }> = await axios.post(`${FRAUD_BASE_URL}/blacklist/${blacklistId}`, updates);
      return response?.data ?? { success: false };
    } catch (error) {
      LogService.logError(new Error('Error updating blacklist entry', { cause: error }));
      throw error;
    }
  },

  /**
   * Remove address from blacklist (hard delete)
   * @param {number} blacklistId - ID of blacklist entry to remove
   * @returns {Promise<void>}
   */
  removeFromBlacklist: async (blacklistId: number): Promise<void> => {
    try {
      await axios.delete(`${FRAUD_BASE_URL}/blacklist/${blacklistId}`);
    } catch (error) {
      LogService.logError(new Error('Error removing address from blacklist', { cause: error }));
      throw error;
    }
  },

  /**
   * Add address to blacklist from an order and mark it as fraud
   * @param {number} orderId - Order ID to blacklist from
   * @param {string} reason - Reason for blacklisting
   * @returns {Promise<{blacklistedAddressID: number}>} Created blacklist entry
   */
  addToBlacklistFromOrder: async (orderId: number, reason: string): Promise<{ blacklistedAddressID: number }> => {
    try {
      const response: AxiosResponse<{ blacklistedAddressID: number }> = await axios.post(`${FRAUD_BASE_URL}/manualfraud`, {
        orderId,
        reason,
      });
      return response.data;
    } catch (error) {
      LogService.logError(new Error('Error adding address to blacklist from order', { cause: error }));
      throw error;
    }
  },

  /**
   * Remove a fraud indicator from an order with audit trail
   * @param {number} orderId - Order ID (salesOrderId)
   * @param {string} fraudIndicatorCode - Fraud indicator code to remove
   * @param {string} reason - Reason for removal (required for audit trail)
   * @returns {Promise<RemoveFraudTagResult>} Remove indicator result
   */
  removeFraudTag: async (orderId: number, fraudIndicatorCode: string, reason: string): Promise<RemoveFraudTagResult> => {
    try {
      const response: AxiosResponse<RemoveFraudTagResult> = await axios.post(`${FRAUD_BASE_URL}/indicator/remove`, {
        orderId,
        fraudIndicatorCode,
        reason,
      });
      return response.data;
    } catch (error) {
      LogService.logError(new Error('Error removing fraud tag', { cause: error }));
      throw error;
    }
  },
};
